import {Component, DestroyRef, inject, OnInit, signal, TemplateRef, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PendingUsersService} from "../../../services/pending-users.service";
import {PENDING_USERS_TABLE_COLUMNS} from "./pending-users-table.config";
import {TableData} from "../../../interfaces/table/table-data";
import {auditTime, distinctUntilChanged, Subject, switchMap, tap} from "rxjs";
import {TableParams} from "../../../interfaces/table/table-params";
import {ObjectUtils} from "../../../utils/object-utils";
import {TableAction} from "../../../interfaces/table/table-action";
import {SessionService} from "../../../services/session.service";
import {ToastService} from "../../../services/toast.service";
import {Permissions} from "../../../enums/permissions";
import {ParamsWrapper} from "../../../interfaces/params-wrapper";

@Component({
  selector: 'app-pending-users',
  templateUrl: './pending-users.component.html',
  standalone: false
})
export class PendingUsersComponent implements OnInit {
  currentUser: any;
  search: string = '';
  tableData: TableData = {data: [], total: 0};
  readonly tableColumns = PENDING_USERS_TABLE_COLUMNS;
  loadingTableData = signal<boolean>(true);
  loadInvitedUsers$ = new Subject<ParamsWrapper<{ tableParams: TableParams, search: string }>>();
  tableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  tableActions: TableAction[] = [];
  @ViewChild('approveIcon', {static: true}) approveIcon!: TemplateRef<any>;
  @ViewChild('rejectIcon', {static: true}) rejectIcon!: TemplateRef<any>;

  approvingUserId: string | null = null;

  private readonly pendingUserService = inject(PendingUsersService);
  private readonly sessionService = inject(SessionService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser = res.user);
  }

  ngOnInit() {
    this.initObservable();
    this.initActions();
    this.refreshData();
  }

  initObservable() {
    this.loadInvitedUsers$.pipe(
      distinctUntilChanged((prev, curr) => {
        if (curr.forceRefresh) return false;
        return ObjectUtils.deepEqual(prev.params, curr.params);
      }),
      tap(_ => this.loadingTableData.set(true)),
      auditTime(0),
      switchMap(value => {
        const {tableParams, search} = value.params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.pendingUserService.getPendingUsers(tableParams.pageSize, offset, search, tableParams.sortParams);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      this.loadingTableData.set(false);
      const data: any = response.Data || response.data;
      this.tableData = {
        data: data.map((item: any) => ({
          cp_pending_user_id: item.cp_pending_user_id,
          first_name: item.first_name,
          last_name: item.last_name,
          work_email: item.work_email,
          phone: item.phone
        })),
        total: data.length !== 0 ? data[0].total_pending_users : 0,
      };
    });
  }

  initActions() {
    this.tableActions = [
      {
        execute: (row) => this.approvingUserId = row.cp_pending_user_id,
        icon: this.approveIcon,
        textColor: '#ffffff',
        backgroundColor: '#16a34a',
        hide: _ => !this.hasPermission(Permissions.PendingUserActions)
      },
      {
        execute: (row) => this.rejectUser(row.cp_pending_user_id),
        icon: this.rejectIcon,
        textColor: '#ffffff',
        backgroundColor: '#dc2626',
        hide: _ => !this.hasPermission(Permissions.PendingUserActions)
      },
    ];
  }

  refreshData(forceRefresh: boolean = false) {
    const tableParams = {...this.tableParams}
    const params = {tableParams, search: this.search};
    this.loadInvitedUsers$.next({params, forceRefresh});
  }

  onTableParamChange(params: TableParams) {
    this.tableParams = params;
    this.refreshData();
  }

  hasPermission(permission: string): any {
    if (!this.currentUser) return false;
    const permissions = this.currentUser?.permission.split(',');
    return permissions.includes(permission);
  }

  approveUser(roleId: string, locationIds: string[]) {
    this.pendingUserService.approvePendingUsers(this.approvingUserId!, roleId, locationIds).then((res: any) => {
      if (!res.IsSuccess) {
        this.toastService.showToast(res.error.error, 'error');
        return;
      }
      this.toastService.showToast('Approved pending user', 'success');
      this.refreshData(true);
    });
    this.approvingUserId = null;
  }

  cancelUserApproving() {
    this.approvingUserId = null;
  }

  rejectUser(id: string) {
    this.pendingUserService.rejectPendingUsers(id).then((res: any) => {
      if (!res.IsSuccess) {
        this.toastService.showToast(res.error.error, 'error');
        return;
      }
      this.toastService.showToast('Rejected pending user', 'success');
      this.refreshData(true);
    })
  }
}
